import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { retentionApi } from "../services/retention.api";
import type { RetentionRun } from "../types";

/** Barcha retention so'rovlarining umumiy kesh prefiksi. */
const KESH = ["retention", "runs"];

/**
 * OCHIQ yugurishlar — kartalar bo'limi.
 *
 * ⚠️ **Uch alohida so'rov, bitta emas.** Backend `status` bo'yicha
 * AYNAN TENGLIK filtrini beradi (`eq`), ya'ni «uchtadan biri»ni bitta
 * so'rovda so'rab bo'lmaydi. Ochiq yugurish tur boshiga eng ko'p
 * BITTA bo'lgani uchun (`retention_runs_open_uniq`) uchala so'rov ham
 * kichik.
 */
export function useOchiqYugurishlar() {
	return useQuery({
		queryKey: [...KESH, "ochiq"],
		queryFn: async () => {
			const javoblar = await Promise.all([
				retentionApi.list({ status: "dry_run", limit: 20 }),
				retentionApi.list({ status: "confirmed", limit: 20 }),
				retentionApi.list({ status: "running", limit: 20 }),
			]);

			return javoblar.flatMap((javob) => javob.items);
		},
	});
}

/** Tarix — `done` va `failed`; server tomonidan sahifalanadi. */
export function useYugurishTarixi() {
	return usePaginatedQuery<RetentionRun>({
		queryKey: [...KESH, "tarix"],
		url: "/retention/runs",
	});
}

function useRetentionMutation(
	mutationFn: (input: { id: string; reason: string }) => Promise<RetentionRun>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			message.success(successMessage);

			// ⚠️ Promise QAYTARILADI — modal `isPending` tugagunicha ochiq
			// qoladi va admin eski holatni bir zumga ko'rmaydi.
			return queryClient.invalidateQueries({ queryKey: KESH });
		},
		onError: (error) => {
			// Server aniq sababni beradi (`notConfirmable`, `runNotFound`).
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useConfirmRun() {
	return useRetentionMutation(
		({ id, reason }) => retentionApi.confirm(id, reason),
		"Yugurish tasdiqlandi — keyingi tikda bajariladi",
		"Yugurishni tasdiqlab bo'lmadi"
	);
}

export function useCancelRun() {
	return useRetentionMutation(
		({ id, reason }) => retentionApi.cancel(id, reason),
		"Yugurish bekor qilindi",
		"Yugurishni bekor qilib bo'lmadi"
	);
}
